import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuthStore } from '../store/authStore';
import { toast } from 'sonner';
import {
  Check, Zap, Crown, Shield, ArrowLeft, Loader2,
  CreditCard, AlertTriangle, Star, X
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const TIER_ORDER = ['free', 'starter', 'professional', 'enterprise'];

const TIER_STYLE = {
  free:          { icon: Shield, accent: 'text-slate-600',  bg: 'bg-slate-50',   border: 'border-slate-200',  badge: 'bg-slate-100 text-slate-700' },
  starter:       { icon: Zap,    accent: 'text-blue-600',   bg: 'bg-blue-50',    border: 'border-blue-200',   badge: 'bg-blue-100 text-blue-700' },
  professional:  { icon: Crown,  accent: 'text-violet-600', bg: 'bg-violet-50',  border: 'border-violet-200', badge: 'bg-violet-100 text-violet-700' },
  enterprise:    { icon: Star,   accent: 'text-amber-600',  bg: 'bg-amber-50',   border: 'border-amber-200',  badge: 'bg-amber-100 text-amber-700' },
};

const PLAN_HIGHLIGHTS = {
  free: ['3 forms', '25 sessions/month', '100 AI calls', 'Lead scoring'],
  starter: ['20 forms', '200 sessions/month', '1,000 AI calls', 'AI Insights'],
  professional: ['100 forms', '2,000 sessions/month', '10,000 AI calls', 'AI Insights'],
  enterprise: ['Unlimited forms', 'Unlimited sessions', 'Unlimited AI calls', 'Priority support'],
};

export default function BillingPage() {
  const token = useAuthStore((s) => s.token);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [isFree, setIsFree] = useState(true);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [pollingStatus, setPollingStatus] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchBillingData = useCallback(async () => {
    try {
      const [plansRes, subRes, usageRes] = await Promise.all([
        axios.get(`${API}/api/billing/plans`, { headers }),
        axios.get(`${API}/api/billing/subscription`, { headers }),
        axios.get(`${API}/api/billing/usage`, { headers }),
      ]);
      setPlans(plansRes.data.plans || []);
      setSubscription(subRes.data.subscription);
      setCurrentPlan(subRes.data.plan);
      setIsFree(subRes.data.is_free);
      setUsage(usageRes.data);
    } catch {
      toast.error('Failed to load billing data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Poll payment status on return from Stripe
  const pollPaymentStatus = useCallback(async (sessionId, attempt = 0) => {
    const maxAttempts = 8;
    if (attempt >= maxAttempts) {
      setPollingStatus('timeout');
      toast.error('Payment status check timed out. Please refresh the page.');
      return;
    }
    try {
      const res = await axios.get(`${API}/api/billing/checkout/status/${sessionId}`, { headers });
      if (res.data.payment_status === 'paid') {
        setPollingStatus('paid');
        toast.success(`Upgraded to ${res.data.plan_tier} plan!`);
        setSearchParams({});
        fetchBillingData();
        return;
      }
      if (res.data.status === 'expired') {
        setPollingStatus('expired');
        toast.error('Payment session expired.');
        setSearchParams({});
        return;
      }
      setPollingStatus('processing');
      setTimeout(() => pollPaymentStatus(sessionId, attempt + 1), 2000);
    } catch {
      setPollingStatus('error');
      toast.error('Error checking payment status.');
    }
  }, [token]);

  useEffect(() => { fetchBillingData(); }, [fetchBillingData]);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId && token) {
      setPollingStatus('processing');
      pollPaymentStatus(sessionId);
    }
  }, [searchParams, token]);

  const handleCheckout = async (tier) => {
    setCheckoutLoading(tier);
    try {
      const res = await axios.post(`${API}/api/billing/checkout`, {
        plan_tier: tier,
        origin_url: window.location.origin,
      }, { headers });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Checkout failed');
      setCheckoutLoading(null);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription?')) return;
    setCancelLoading(true);
    try {
      await axios.post(`${API}/api/billing/cancel`, {}, { headers });
      toast.success('Subscription canceled');
      fetchBillingData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cancel failed');
    } finally {
      setCancelLoading(false);
    }
  };

  const currentTier = currentPlan?.tier || 'free';
  const currentIdx = TIER_ORDER.indexOf(currentTier);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-950">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950" data-testid="billing-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <Link to="/dashboard" className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors" data-testid="billing-back">
            <ArrowLeft className="h-5 w-5 text-gray-600 dark:text-gray-400" />
          </Link>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Billing & Subscription</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Manage your plan, usage, and payments</p>
          </div>
        </div>

        {/* Payment Processing Banner */}
        {pollingStatus === 'processing' && (
          <div className="mb-6 p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 flex items-center gap-3" data-testid="billing-processing">
            <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
            <span className="text-sm text-blue-700 dark:text-blue-300">Processing your payment...</span>
          </div>
        )}

        {/* Current Plan Card */}
        <div className="mb-8 p-5 rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800" data-testid="current-plan-card">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              {React.createElement(TIER_STYLE[currentTier]?.icon || Shield, {
                className: `h-6 w-6 ${TIER_STYLE[currentTier]?.accent || 'text-gray-600'}`,
              })}
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{currentPlan?.name || 'Free'} Plan</h2>
                  <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${TIER_STYLE[currentTier]?.badge || 'bg-gray-100 text-gray-700'}`}>
                    {subscription?.status === 'active' ? 'Active' : isFree ? 'Free Tier' : subscription?.status || 'Free Tier'}
                  </span>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">{currentPlan?.description}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {currentPlan?.price_amount > 0 && (
                <span className="text-2xl font-bold text-gray-900 dark:text-white">
                  ${currentPlan.price_amount}<span className="text-sm font-normal text-gray-500">/mo</span>
                </span>
              )}
              {!isFree && subscription?.status === 'active' && (
                <button
                  onClick={handleCancel}
                  disabled={cancelLoading}
                  className="px-3 py-1.5 text-sm rounded-lg border border-red-200 text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50"
                  data-testid="cancel-subscription-btn"
                >
                  {cancelLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Cancel'}
                </button>
              )}
            </div>
          </div>

          {/* Period Info */}
          {subscription?.current_period_end && (
            <p className="mt-3 text-xs text-gray-400">
              Current period ends: {new Date(subscription.current_period_end).toLocaleDateString()}
            </p>
          )}
        </div>

        {/* Plan Cards */}
        <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4">Choose a Plan</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10" data-testid="plan-cards">
          {TIER_ORDER.map((tier) => {
            const plan = plans.find((p) => p.tier === tier) || DEFAULT_PLANS_FE[tier];
            if (!plan) return null;
            const style = TIER_STYLE[tier];
            const Icon = style.icon;
            const tierIdx = TIER_ORDER.indexOf(tier);
            const isCurrent = tier === currentTier;
            const isUpgrade = tierIdx > currentIdx;
            const isDowngrade = tierIdx < currentIdx && tierIdx > 0;

            return (
              <div
                key={tier}
                className={`relative rounded-xl border p-5 flex flex-col transition-all ${
                  isCurrent
                    ? `${style.border} ${style.bg} ring-2 ring-offset-1 ring-${tier === 'free' ? 'slate' : tier === 'starter' ? 'blue' : tier === 'professional' ? 'violet' : 'amber'}-300`
                    : 'border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 hover:border-gray-300 dark:hover:border-gray-700'
                }`}
                data-testid={`plan-card-${tier}`}
              >
                {tier === 'professional' && (
                  <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full text-[10px] font-semibold bg-violet-600 text-white">
                    Popular
                  </span>
                )}
                <div className="flex items-center gap-2 mb-3">
                  <Icon className={`h-5 w-5 ${style.accent}`} />
                  <span className="font-semibold text-gray-900 dark:text-white">{plan.name}</span>
                </div>
                <div className="mb-4">
                  {plan.price_amount > 0 ? (
                    <span className="text-3xl font-bold text-gray-900 dark:text-white">
                      ${plan.price_amount}<span className="text-sm font-normal text-gray-500">/mo</span>
                    </span>
                  ) : (
                    <span className="text-3xl font-bold text-gray-900 dark:text-white">Free</span>
                  )}
                </div>
                <ul className="space-y-2 mb-5 flex-1">
                  {(PLAN_HIGHLIGHTS[tier] || []).map((feat, i) => (
                    <li key={`item-${i}`} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-300">
                      <Check className={`h-4 w-4 mt-0.5 shrink-0 ${style.accent}`} />
                      <span>{feat}</span>
                    </li>
                  ))}
                </ul>
                {isCurrent ? (
                  <div className="py-2 text-center text-sm font-medium text-gray-500 dark:text-gray-400 rounded-lg bg-gray-100 dark:bg-gray-800" data-testid={`plan-current-${tier}`}>
                    Current Plan
                  </div>
                ) : isUpgrade ? (
                  <button
                    onClick={() => handleCheckout(tier)}
                    disabled={!!checkoutLoading}
                    className={`w-full py-2 rounded-lg text-sm font-medium text-white transition-colors disabled:opacity-50 ${
                      tier === 'starter' ? 'bg-blue-600 hover:bg-blue-700' :
                      tier === 'professional' ? 'bg-violet-600 hover:bg-violet-700' :
                      'bg-amber-600 hover:bg-amber-700'
                    }`}
                    data-testid={`plan-upgrade-${tier}`}
                  >
                    {checkoutLoading === tier ? (
                      <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                    ) : (
                      <>Upgrade <CreditCard className="h-3.5 w-3.5 inline ml-1" /></>
                    )}
                  </button>
                ) : isDowngrade ? (
                  <button
                    onClick={() => handleCheckout(tier)}
                    disabled={!!checkoutLoading}
                    className="w-full py-2 rounded-lg text-sm font-medium border border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
                    data-testid={`plan-downgrade-${tier}`}
                  >
                    {checkoutLoading === tier ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : 'Switch'}
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>

        {/* Usage Section */}
        {usage && (
          <div className="p-5 rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800" data-testid="usage-section">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-medium text-gray-900 dark:text-white">Usage</h3>
              <span className="text-xs text-gray-400">
                {usage.plan_name} plan{usage.reset_at ? ` | Resets ${new Date(usage.reset_at).toLocaleDateString()}` : ''}
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {usage.usage?.forms_created && (
                <UsageMeter
                  label="Forms"
                  current={usage.usage.forms_created.current}
                  limit={usage.usage.forms_created.limit}
                  period={usage.usage.forms_created.period === 'lifetime' ? 'total' : 'this month'}
                />
              )}
              {usage.usage?.sessions_completed && (
                <UsageMeter
                  label="Sessions"
                  current={usage.usage.sessions_completed.current}
                  limit={usage.usage.sessions_completed.limit}
                  period="this month"
                />
              )}
            </div>
          </div>
        )}

        {/* Insights Access Notice */}
        {isFree && (
          <div className="mt-6 p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 flex items-start gap-3" data-testid="insights-upgrade-notice">
            <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800 dark:text-amber-200">AI Insights requires a paid plan</p>
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">Upgrade to Starter or above to unlock AI-powered session insights, lead qualification analysis, and aggregate reporting.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function UsageMeter({ label, current, limit, period }) {
  const isUnlimited = limit === -1;
  const pct = isUnlimited ? 0 : limit > 0 ? Math.min((current / limit) * 100, 100) : 0;
  const isWarning = pct >= 80 && pct < 100;
  const isBlocked = pct >= 100;
  const barColor = isBlocked ? 'bg-red-500' : isWarning ? 'bg-amber-500' : 'bg-blue-500';
  const bgRing = isBlocked ? 'border-red-200 dark:border-red-800' : isWarning ? 'border-amber-200 dark:border-amber-800' : '';

  return (
    <div className={`p-3 rounded-lg bg-gray-50 dark:bg-gray-800 border ${bgRing || 'border-transparent'}`} data-testid={`usage-meter-${label.toLowerCase()}`}>
      <div className="flex justify-between items-baseline mb-1">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
        <span className="text-xs text-gray-500">
          {current}{isUnlimited ? ' / Unlimited' : ` / ${limit}`}
          {period ? ` ${period}` : ''}
        </span>
      </div>
      {!isUnlimited ? (
        <div className="h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-300 ${barColor}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      ) : (
        <div className="h-2 rounded-full bg-green-200 dark:bg-green-800">
          <div className="h-full rounded-full bg-green-500 w-full" />
        </div>
      )}
      {isWarning && !isBlocked && (
        <p className="mt-1.5 text-xs text-amber-600 dark:text-amber-400 font-medium" data-testid={`usage-warning-${label.toLowerCase()}`}>
          Approaching limit — {limit - current} remaining
        </p>
      )}
      {isBlocked && (
        <p className="mt-1.5 text-xs text-red-600 dark:text-red-400 font-medium" data-testid={`usage-blocked-${label.toLowerCase()}`}>
          Limit reached — upgrade to continue
        </p>
      )}
    </div>
  );
}

// Fallback plan data for rendering when DB plans haven't loaded
const DEFAULT_PLANS_FE = {
  free: { name: 'Free', tier: 'free', price_amount: 0, description: 'Get started with basic features' },
  starter: { name: 'Starter', tier: 'starter', price_amount: 29, description: 'For growing teams' },
  professional: { name: 'Professional', tier: 'professional', price_amount: 99, description: 'For serious businesses' },
  enterprise: { name: 'Enterprise', tier: 'enterprise', price_amount: 299, description: 'Unlimited usage with priority support' },
};
